import axios from 'axios';

import { createApiPath } from '@/shared/api/endpoints';
import { apiClient } from '@/shared/api/http-client';

/** 처방전 이미지 1장 최대 크기 — 서버도 완료 때 S3 HEAD로 확인해 넘으면 지우고 400을 준다 */
export const MAX_PRESCRIPTION_IMAGE_BYTES = 10 * 1024 * 1024;

/**
 * 업로드 URL 발급 응답 항목 — URL은 10분 뒤 만료.
 * uploadKey · shortKey · uploadToken은 완료 요청에 그대로 보낸다 (이 사용자 · 거래처에서만 유효)
 */
export interface PrescriptionUploadTarget {
  filename: string;
  presignedUrl: string;
  uploadKey: string;
  shortKey: string;
  uploadToken: string;
}

export type UploadedImageKey = Pick<
  PrescriptionUploadTarget,
  'shortKey' | 'uploadKey' | 'uploadToken'
>;

/** 업로드 완료 응답 — 년월은 서버가 10일 마감 기준으로 정한다 */
export interface UploadCompleteResponse {
  imageCount: number;
  registrationDate: string;
  prescriptionYearMonth: string;
  registrationYearMonth: string;
  settlementYearMonth: string;
}

/**
 * 완료 요청 에러 코드 (400)
 * - INVALID_PRESCRIPTION_IMAGE_UPLOAD_KEY: 키 · 토큰 불일치 (거래처를 바꿨으면 발급부터 다시)
 * - PRESCRIPTION_IMAGE_NOT_UPLOADED: S3 PUT이 끝나지 않은 파일
 * - PRESCRIPTION_IMAGE_TOO_LARGE: 10MB 초과 — 올린 파일은 서버가 지우고 요청 전체가 저장되지 않는다
 */
export const UPLOAD_ERROR = {
  INVALID_KEY: 'INVALID_PRESCRIPTION_IMAGE_UPLOAD_KEY',
  NOT_UPLOADED: 'PRESCRIPTION_IMAGE_NOT_UPLOADED',
  TOO_LARGE: 'PRESCRIPTION_IMAGE_TOO_LARGE',
} as const;

/** S3는 presigned URL을 만들 때의 Content-Type과 같아야 하므로 확장자로 맞춘다 */
export const getContentTypeByFilename = (filename: string) => {
  const ext = filename.split('.').pop()?.toLowerCase();
  if (ext === 'png') return 'image/png';
  if (ext === 'pdf') return 'application/pdf';
  return 'image/jpeg';
};

const path = createApiPath.businessRelationships;

export const prescriptionApi = {
  presignedUrls: async ({
    relationshipId,
    filenames,
  }: {
    relationshipId: number;
    filenames: string[];
  }) => {
    const { data } = await apiClient.post<{
      targets: PrescriptionUploadTarget[];
    }>(path.presignedUrls(relationshipId), { filenames });
    return data.targets;
  },

  /**
   * S3 PUT — 우리 API가 아니므로 apiClient(Authorization 헤더 · 15초 타임아웃)를 쓰지 않는다.
   * 헤더가 붙으면 presigned 서명이 깨진다.
   */
  uploadToS3: async (
    presignedUrl: string,
    file: File,
    onProgress?: (percent: number) => void,
  ) => {
    await axios.put(presignedUrl, file, {
      headers: { 'Content-Type': getContentTypeByFilename(file.name) },
      onUploadProgress: (e) => {
        if (onProgress && e.total) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      },
    });
  },

  /** 전부 저장되거나 전부 실패한다. 같은 요청을 다시 보내도 중복 저장 없이 200 */
  uploadComplete: async ({
    relationshipId,
    images,
  }: {
    relationshipId: number;
    images: UploadedImageKey[];
  }) => {
    const { data } = await apiClient.post<UploadCompleteResponse>(
      path.prescriptionImages(relationshipId),
      { images },
    );
    return data;
  },

  /** 204 · 400 PRESCRIPTION_ALREADY_EXISTS(확정 처방 있음) · 404 S3_FILE_KEY_NOT_FOUND */
  deleteImage: async (imageCode: string) => {
    await apiClient.delete(createApiPath.prescriptions.deleteImage(imageCode));
  },
};
